<template>
  <!-- Нижняя панель редактора маршрута: Закрыть · Файл · Действия · Вид.
       Инструменты рисования - в правой панели (RouteEditorContextPanel).
       На мобильном живёт в нижнем баре App.vue, на десктопе - внизу карты
       (MapView.vue). Меню открываются вверх, Esc/тап мимо закрывают. -->
  <div ref="rootEl" class="relative flex items-stretch justify-around w-full h-[58px]" role="toolbar" :aria-label="$t('map.routeEditor.actionsLabel')">
    <button type="button" data-bar-item="close" class="bar-btn" :aria-label="$t('map.routeEditor.close')" @click="$emit('close')">
      <span class="pill"><X class="w-[22px] h-[22px]" aria-hidden="true" /></span><span class="label">{{ $t('map.routeEditor.closeShort') }}</span>
    </button>

    <div v-for="menu in MENUS" :key="menu.key" class="relative flex-1 flex">
      <button
        type="button"
        :data-bar-item="menu.key"
        :data-menu-trigger="menu.key"
        class="bar-btn"
        :class="open === menu.key && 'bar-btn--on'"
        aria-haspopup="menu"
        :aria-expanded="open === menu.key"
        @click="toggle(menu.key)"
      >
        <span class="pill"><component :is="menu.icon" class="w-[22px] h-[22px]" aria-hidden="true" /></span>
        <span class="label">{{ $t(menu.label) }}</span>
      </button>

      <div
        v-if="open === menu.key"
        :data-menu="menu.key"
        role="menu"
        :aria-label="$t(menu.label)"
        class="menu-panel paper-card"
        :class="menu.key === 'view' && 'menu-panel--end'"
        @keydown="onMenuKeydown"
      >
        <template v-if="menu.key === 'file'">
          <button type="button" role="menuitem" data-action="new" class="menu-item" @click="run(() => $emit('new'))"><FilePlus class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.hintNew') }}</button>
          <div class="menu-sep" role="separator" />
          <button type="button" role="menuitem" data-action="save" class="menu-item" @click="run(() => $emit('save'))"><Save class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.save') }}</button>
          <button type="button" role="menuitem" data-action="save-as" class="menu-item" @click="run(() => $emit('save-as'))"><Copy class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.saveAs') }}</button>
          <button type="button" role="menuitem" data-action="export" class="menu-item" @click="run(() => $emit('export'))"><Download class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.downloadGpx') }}</button>
          <p class="menu-heading">{{ $t('map.routeEditor.importLabel') }}</p>
          <button type="button" role="menuitem" data-action="gpx" class="menu-item" @click="run(() => $emit('import', 'gpx'))"><FileUp class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.importFromGpx') }}</button>
          <button type="button" role="menuitem" data-action="public" class="menu-item" @click="run(() => $emit('import', 'public'))"><BookOpen class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.importFromCatalogue') }}</button>
          <button type="button" role="menuitem" data-action="mine" class="menu-item" @click="run(() => $emit('import', 'mine'))"><FolderOpen class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.importFromMine') }}</button>
        </template>
        <template v-else-if="menu.key === 'actions'">
          <button type="button" role="menuitem" data-action="reverse" class="menu-item" :disabled="!hasLine" @click="run(() => editor.reverseRoute())"><ArrowLeftRight class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.labelReverse') }}</button>
          <button type="button" role="menuitem" data-action="closeLoop" class="menu-item" :disabled="!editor.canCloseLoop.value" @click="run(() => void editor.closeLoop())"><Link class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.labelCloseLoop') }}</button>
          <div class="menu-sep" role="separator" />
          <button type="button" role="menuitem" data-action="info" class="menu-item" @click="run(toggleHints)"><Info class="w-4 h-4" aria-hidden="true" />{{ $t('map.routeEditor.labelInfo') }}</button>
        </template>
        <template v-else>
          <button type="button" role="menuitemcheckbox" data-action="stats" :aria-checked="editor.showStatsPanel.value" class="menu-item" @click="editor.showStatsPanel.value = !editor.showStatsPanel.value">
            <Check class="w-4 h-4" :class="!editor.showStatsPanel.value && 'opacity-0'" aria-hidden="true" />{{ $t('map.routeEditor.viewStatsPanel') }}
          </button>
          <p class="menu-heading">{{ $t('map.routeEditor.viewLineStyle') }}</p>
          <button type="button" role="menuitemradio" data-action="solid" :aria-checked="editor.lineStyleMode.value === 'single'" class="menu-item" @click="editor.setLineStyleMode('single')">
            <Check class="w-4 h-4" :class="editor.lineStyleMode.value !== 'single' && 'opacity-0'" aria-hidden="true" />{{ $t('map.routeEditor.viewLineSolid') }}
          </button>
          <button type="button" role="menuitemradio" data-action="gradient" :aria-checked="editor.lineStyleMode.value === 'gradient'" class="menu-item" @click="editor.setLineStyleMode('gradient')">
            <Check class="w-4 h-4" :class="editor.lineStyleMode.value !== 'gradient' && 'opacity-0'" aria-hidden="true" />{{ $t('map.routeEditor.viewLineGradient') }}
          </button>
          <button type="button" role="menuitemcheckbox" data-action="arrows" :aria-checked="editor.showDirectionArrows.value" class="menu-item" @click="editor.setShowDirectionArrows(!editor.showDirectionArrows.value)">
            <Check class="w-4 h-4" :class="!editor.showDirectionArrows.value && 'opacity-0'" aria-hidden="true" />{{ $t('map.routeEditor.viewDirectionArrows') }}
          </button>
          <button type="button" role="menuitemcheckbox" data-action="3d" :aria-checked="is3D" class="menu-item" @click="run(() => $emit('toggle-3d'))">
            <Check class="w-4 h-4" :class="!is3D && 'opacity-0'" aria-hidden="true" />{{ $t('map.routeEditor.view3d') }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import {
  ArrowLeftRight,
  BookOpen,
  Check,
  Copy,
  Download,
  Eye,
  File,
  FilePlus,
  FileUp,
  FolderOpen,
  Info,
  Link,
  Save,
  Wand2,
  X,
} from "lucide-vue-next";
import type { useRouteEditor } from "../composables/useRouteEditor";
import { useRouteEditorHints } from "../composables/useRouteEditorHints";

type MenuKey = "file" | "actions" | "view";
export type ImportTab = "gpx" | "public" | "mine";

const props = defineProps<{ editor: ReturnType<typeof useRouteEditor>; is3D: boolean }>();
defineEmits<{
  (e: "close" | "save" | "save-as" | "export" | "toggle-3d" | "new"): void;
  (e: "import", tab: ImportTab): void;
}>();

const MENUS: { key: MenuKey; label: string; icon: unknown }[] = [
  { key: "file", label: "map.routeEditor.menuFile", icon: File },
  { key: "actions", label: "map.routeEditor.menuActions", icon: Wand2 },
  { key: "view", label: "map.routeEditor.menuView", icon: Eye },
];

const { toggleHints } = useRouteEditorHints();
const hasLine = computed(() => props.editor.vertices.value.length >= 2);

const open = ref<MenuKey | null>(null);
const rootEl = ref<HTMLElement | null>(null);

const menuItems = () => Array.from(rootEl.value?.querySelectorAll<HTMLElement>('[role="menu"] [role^="menuitem"]:not(:disabled)') ?? []);

const toggle = async (key: MenuKey) => {
  open.value = open.value === key ? null : key;
  if (open.value) {
    await nextTick();
    menuItems()[0]?.focus();
  }
};

// Действие из меню - выполнить и закрыть меню.
const run = (fn: () => void) => {
  fn();
  open.value = null;
};

const onMenuKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape") {
    e.preventDefault();
    const key = open.value;
    open.value = null;
    rootEl.value?.querySelector<HTMLElement>(`[data-menu-trigger="${key}"]`)?.focus();
    return;
  }
  if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
  e.preventDefault();
  const items = menuItems();
  const idx = items.indexOf(document.activeElement as HTMLElement);
  items[(idx + (e.key === "ArrowDown" ? 1 : items.length - 1)) % items.length]?.focus();
};

const onDocPointerDown = (e: PointerEvent) => {
  if (open.value && rootEl.value && !rootEl.value.contains(e.target as Node)) open.value = null;
};
onMounted(() => document.addEventListener("pointerdown", onDocPointerDown));
onBeforeUnmount(() => document.removeEventListener("pointerdown", onDocPointerDown));
</script>

<style scoped>
/* Как кнопки основной панели и панели действий карточки: плоские, значок в
   «пилюле», подсветка активной; в редакторе подписи остаются. */
.bar-btn {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-width: 0;
  font-size: 11px;
  font-weight: 500;
  color: hsl(var(--muted-foreground));
}
.bar-btn .pill {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 4rem;
  height: 2.25rem;
  border-radius: 9999px;
  transition: background-color 0.15s;
}
.bar-btn .label {
  max-width: 100%;
  padding: 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bar-btn:hover .pill {
  background: hsl(var(--accent));
}
.bar-btn--on {
  color: hsl(var(--primary));
}
.bar-btn--on .pill {
  background: hsl(var(--primary) / 0.1);
}
.menu-panel {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  min-width: 210px;
  padding: 6px;
  z-index: 60;
}
/* Крайнее правое меню прижимаем к правому краю кнопки - иначе на узком
   экране оно уезжает за край. */
.menu-panel--end {
  left: auto;
  right: 4px;
  transform: none;
}
.menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 9px 8px;
  border-radius: calc(var(--radius) - 2px);
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  color: var(--ink);
}
.menu-item:disabled {
  opacity: 0.4;
}
.menu-item:not(:disabled):hover,
.menu-item:focus-visible {
  background: var(--paper-2);
}
.menu-heading {
  padding: 8px 8px 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--ink-2);
}
.menu-sep {
  height: 1px;
  margin: 4px 2px;
  background: hsl(var(--border) / 0.7);
}
</style>
